import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [countdown, setCountdown] = useState(5);
  
  const sessionId = searchParams.get('session_id');
  const method = searchParams.get('method');
  const orderId = searchParams.get('order_id');
  
  useEffect(() => {
    const checkPayment = async () => {
      // For crypto payments
      if (method === 'crypto') {
        setStatus('success');
        return;
      }
      
      // For Stripe payments
      if (!sessionId) {
        setStatus('success');
        return;
      }
      
      // Poll for Stripe payment status
      const pollStatus = async (attempts = 0) => {
        if (attempts >= 5) {
          setStatus('success');
          return;
        }
        
        try {
          const response = await fetch(`${API}/api/payments/stripe/status/${sessionId}`);
          const data = await response.json();
          
          if (data.payment_status === 'paid') {
            setStatus('success');
            setPaymentDetails(data);
          } else {
            setTimeout(() => pollStatus(attempts + 1), 2000);
          }
        } catch (error) {
          setTimeout(() => pollStatus(attempts + 1), 2000);
        }
      };
      
      pollStatus();
    };
    
    checkPayment();
  }, [sessionId, method]);
  
  // Countdown and redirect to onboarding
  useEffect(() => {
    if (status !== 'success') return;
    
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          // Redirect to onboarding
          const params = new URLSearchParams();
          if (sessionId) params.set('session_id', sessionId);
          if (orderId) params.set('order_id', orderId);
          if (method) params.set('method', method);
          navigate(`/onboarding?${params.toString()}`);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [status, navigate, sessionId, orderId, method]);
  
  const handleGoToOnboarding = () => {
    const params = new URLSearchParams();
    if (sessionId) params.set('session_id', sessionId);
    if (orderId) params.set('order_id', orderId);
    if (method) params.set('method', method);
    navigate(`/onboarding?${params.toString()}`);
  };
  
  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        {status === 'checking' ? (
          <>
            <div className="w-16 h-16 border-4 border-white/20 border-t-white rounded-full animate-spin mx-auto mb-8" />
            <h1 className="text-2xl font-bold mb-4">Verificando pago...</h1>
            <p className="text-zinc-500">Por favor espera mientras confirmamos tu pago.</p>
          </>
        ) : (
          <>
            <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold mb-4" data-testid="success-title">
              ¡Pago Exitoso!
            </h1>
            
            <p className="text-lg text-zinc-400 mb-6">
              Gracias por tu compra. Ahora necesitamos algunos datos sobre tu negocio para comenzar a crear tu página web.
            </p>
            
            {paymentDetails && (
              <div className="bg-[#0A0A0A] border border-white/10 p-6 mb-6 text-left">
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-4">
                  Detalles del pago
                </p>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Total</span>
                    <span className="font-bold">${(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency?.toUpperCase()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Estado</span>
                    <span className="text-green-500">Completado</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Onboarding CTA */}
            <div className="bg-[#0A0A0A] border border-white/20 p-6 mb-6">
              <h3 className="font-bold mb-2">Siguiente Paso</h3>
              <p className="text-sm text-zinc-400 mb-4">
                Completa el formulario de onboarding para que podamos comenzar con tu proyecto.
              </p>
              <div className="text-sm text-zinc-500 mb-4">
                Redirigiendo automáticamente en <span className="text-white font-bold">{countdown}</span> segundos...
              </div>
              <Button
                onClick={handleGoToOnboarding}
                className="w-full bg-white text-black hover:bg-zinc-200 rounded-none px-8 py-4 text-sm font-bold tracking-widest uppercase"
                data-testid="onboarding-btn"
              >
                Completar Onboarding
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
            
            <Link to="/" className="text-sm text-zinc-500 hover:text-white transition-colors">
              Volver al inicio
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
